import type { AdaptedProduct } from '@/types'
import { parseNumber } from '@/utils/parsers'

type ReportingFieldCarrier = Record<string, unknown>

type ReportingPropertyEntry = {
  key?: string
  values?: unknown[]
}

type ReportingOptionLookup = Record<
  string,
  {
    id?: string
    label?: string
    optionOptionsId?: string | null
    value?: string
  }
>

export type ReportingOutletFamily =
  | 'reuse'
  | 'recycling'
  | 'recovery'
  | 'disposal'
  | 'unknown'

export type ReportingOutletScope = 'in-situ' | 'ex-situ' | 'unknown'

export type ReportingOutletResolution = {
  label: string
  value: string
  family: ReportingOutletFamily
  scope: ReportingOutletScope
}

export type OperationalValorizationTableRow = {
  id: string
  productID: string
  lotLabel: string
  resourceNumber: string
  resourceLabel: string
  categoryLabel: string
  quantity: number | null
  quantityUnit: string
  volumeM3: number | null
  unitWeightKg: number | null
  totalWeightKg: number | null
  totalWeightTonnes: number | null
  carbonGainKgCo2e: number | null
  reuseRatioPercent: number | null
  reusableWeightKg: number | null
  nonReusedWeightKg: number | null
  targetOutlet: ReportingOutletResolution
  reuseInSituQuantity: number
  reuseInSituVolumeM3: number
  reuseInSituWeightKg: number
  reuseExSituQuantity: number
  reuseExSituVolumeM3: number
  reuseExSituWeightKg: number
  recyclingQuantity: number
  recyclingVolumeM3: number
  recyclingWeightKg: number
  recoveryQuantity: number
  recoveryVolumeM3: number
  recoveryWeightKg: number
  disposalQuantity: number
  disposalVolumeM3: number
  disposalWeightKg: number
  totalValorizedWeightKg: number
  valorizationRate: number | null
  warnings: string[]
}

export type OperationalValorizationTable = {
  rows: OperationalValorizationTableRow[]
  totals: {
    totalWeightKg: number
    totalWeightTonnes: number
    carbonGainKgCo2e: number
    reusableWeightKg: number
    nonReusedWeightKg: number
    reuseInSituWeightKg: number
    reuseExSituWeightKg: number
    recyclingWeightKg: number
    recoveryWeightKg: number
    disposalWeightKg: number
    totalValorizedWeightKg: number
    valorizationRate: number | null
  }
}

export type BuildOperationalValorizationTableOptions = {
  outletOptionsByID?: ReportingOptionLookup
}

export type OperationalResourceSheetItem = {
  key: string
  label: string
  value: string | number
  unit?: string
}

export type OperationalResourceSheet = {
  id: string
  productID: string
  resourceNumber: string
  title: string
  lotLabel: string
  categoryLabel: string
  pictures: string[]
  characteristics: OperationalResourceSheetItem[]
  dimensions: OperationalResourceSheetItem[]
  preparation: OperationalResourceSheetItem[]
  circularEconomyIndicators: OperationalResourceSheetItem[]
  reuseTracks: OperationalResourceSheetItem[]
  targetOutlet: ReportingOutletResolution
  warnings: string[]
}

export type BuildOperationalResourceSheetsOptions =
  BuildOperationalValorizationTableOptions

const TARGET_OUTLET_KEY = 'target_outlet'

const RESOURCE_INSTRUCTION_DEFINITIONS = [
  { key: 'instructionAssembly', label: 'Mode de fixation' },
  { key: 'instructionRemoval', label: 'Méthode de dépose' },
  { key: 'instructionTransport', label: 'Transport' },
  { key: 'instructionStorage', label: 'Stockage' },
  { key: 'instructionConditioning', label: 'Conditionnement' },
  { key: 'instructionSafety', label: 'Risques et précautions' },
] as const

const REUSE_TRACK_DEFINITIONS = [
  { key: 'instructionPotential', label: 'Pistes de réemploi' },
] as const

const normalizeLabel = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const normalizeSearchText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const round = (value: number, precision = 3): number => {
  const factor = 10 ** precision
  return Math.round((value + Number.EPSILON) * factor) / factor
}

const clampPercent = (value: number): number =>
  Math.min(Math.max(value, 0), 100)

const isReportingResource = (product: AdaptedProduct): boolean =>
  product.type !== 'done' && product.progress !== 'done'

const readPropertyEntry = (
  product: ReportingFieldCarrier,
  key: string,
): ReportingPropertyEntry | undefined =>
  Array.isArray(product.properties)
    ? (product.properties as ReportingPropertyEntry[]).find(
        (entry) => entry?.key === key,
      )
    : undefined

const readFieldValue = (
  product: ReportingFieldCarrier,
  key: string,
): unknown => {
  const directValue = product[key]

  if (
    directValue &&
    typeof directValue === 'object' &&
    'value' in directValue &&
    typeof directValue.value !== 'undefined'
  ) {
    return directValue.value
  }

  const propertyEntry = readPropertyEntry(product, key)
  if (propertyEntry && Array.isArray(propertyEntry.values)) {
    if (propertyEntry.values.length === 1) return propertyEntry.values[0]
    return propertyEntry.values
  }

  return directValue
}

const readFieldLabel = (
  product: ReportingFieldCarrier,
  key: string,
): string => {
  const directValue = product[key]
  if (
    directValue &&
    typeof directValue === 'object' &&
    'label' in directValue &&
    typeof directValue.label === 'string'
  ) {
    return directValue.label.trim()
  }

  const value = readFieldValue(product, key)
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : ''
}

const readNumericField = (
  product: ReportingFieldCarrier,
  key: string,
): number | null => parseNumber(readFieldValue(product, key))

const readQuantity = (product: ReportingFieldCarrier): number | null =>
  readNumericField(product, 'quantity.number') ??
  parseNumber(
    product.quantity &&
      typeof product.quantity === 'object' &&
      'number' in product.quantity
      ? product.quantity.number
      : null,
  )

const readQuantityUnit = (product: ReportingFieldCarrier): string => {
  const flatUnit = readFieldLabel(product, 'quantity.unit')
  if (flatUnit) return flatUnit

  const quantity = product.quantity
  if (
    quantity &&
    typeof quantity === 'object' &&
    'unit' in quantity &&
    typeof quantity.unit === 'string'
  ) {
    return quantity.unit
  }

  return ''
}

const readNestedDimensionNumber = (
  product: ReportingFieldCarrier,
  key: 'width' | 'height' | 'length' | 'volume',
): number | null => {
  const dimension = product.dimension
  if (!dimension || typeof dimension !== 'object' || !(key in dimension)) {
    return null
  }

  return parseNumber(dimension[key])
}

const readVolumeM3 = (product: ReportingFieldCarrier): number | null =>
  readNumericField(product, 'dimension.volume') ??
  readNestedDimensionNumber(product, 'volume')

const readProductString = (
  product: ReportingFieldCarrier,
  key: string,
): string => readFieldLabel(product, key) || normalizeLabel(product[key])

const readProductStringList = (
  product: ReportingFieldCarrier,
  key: string,
): string[] => {
  const value = product[key]

  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (
          entry &&
          typeof entry === 'object' &&
          'label' in entry &&
          typeof entry.label === 'string'
        ) {
          return entry.label.trim()
        }

        if (typeof entry === 'string' || typeof entry === 'number') {
          return String(entry).trim()
        }

        return ''
      })
      .filter(Boolean)
  }

  const label = readProductString(product, key)
  return label ? [label] : []
}

const readPictures = (product: ReportingFieldCarrier): string[] =>
  Array.isArray(product.pictures)
    ? product.pictures.filter(
        (picture): picture is string =>
          typeof picture === 'string' && picture.trim().length > 0,
      )
    : []

const pushOutletToken = (
  tokens: string[],
  value: unknown,
  outletOptionsByID?: ReportingOptionLookup,
  seenOptionIds = new Set<string>(),
) => {
  if (Array.isArray(value)) {
    value.forEach((entry) =>
      pushOutletToken(tokens, entry, outletOptionsByID, seenOptionIds),
    )
    return
  }

  if (value && typeof value === 'object') {
    if ('value' in value)
      pushOutletToken(tokens, value.value, outletOptionsByID, seenOptionIds)
    if ('label' in value)
      pushOutletToken(tokens, value.label, outletOptionsByID, seenOptionIds)
    return
  }

  if (typeof value !== 'string' && typeof value !== 'number') return

  const normalizedValue = String(value).trim()
  if (!normalizedValue) return

  tokens.push(normalizedValue)
  const option = outletOptionsByID?.[normalizedValue]
  if (!option || seenOptionIds.has(normalizedValue)) return

  seenOptionIds.add(normalizedValue)
  if (option?.value) tokens.push(option.value)
  if (option?.label) tokens.push(option.label)
  if (option.optionOptionsId) {
    pushOutletToken(
      tokens,
      option.optionOptionsId,
      outletOptionsByID,
      seenOptionIds,
    )
  }
}

export const resolveReportingOutletFamily = (
  value: unknown,
): ReportingOutletFamily => {
  const normalized = normalizeSearchText(value)
  if (normalized.includes('reuse') || normalized.includes('reemploi')) {
    return 'reuse'
  }
  if (normalized.includes('recycling') || normalized.includes('recyclage')) {
    return 'recycling'
  }
  if (normalized.includes('recovery') || normalized.includes('valorisation')) {
    return 'recovery'
  }
  if (normalized.includes('disposal') || normalized.includes('elimination')) {
    return 'disposal'
  }

  return 'unknown'
}

export const resolveReportingOutletScope = (
  value: unknown,
): ReportingOutletScope => {
  const normalized = normalizeSearchText(value)
  if (normalized.includes('in-situ') || normalized.includes('in situ')) {
    return 'in-situ'
  }
  if (normalized.includes('ex-situ') || normalized.includes('ex situ')) {
    return 'ex-situ'
  }

  return 'unknown'
}

const resolveOutletOptionLabel = (
  option:
    | {
        label?: string
        optionOptionsId?: string | null
        value?: string
      }
    | undefined,
  outletOptionsByID?: ReportingOptionLookup,
) => {
  const label = normalizeLabel(option?.label) || normalizeLabel(option?.value)
  if (!label) return ''

  const parentOption =
    option?.optionOptionsId && outletOptionsByID?.[option.optionOptionsId]
      ? outletOptionsByID[option.optionOptionsId]
      : undefined
  const parentLabel =
    normalizeLabel(parentOption?.label) || normalizeLabel(parentOption?.value)

  if (
    !parentLabel ||
    normalizeSearchText(label).includes(normalizeSearchText(parentLabel))
  ) {
    return label
  }

  return `${parentLabel} ${label}`
}

const resolveTargetOutlet = (
  product: ReportingFieldCarrier,
  options?: BuildOperationalValorizationTableOptions,
): ReportingOutletResolution => {
  const fieldValue = readFieldValue(product, TARGET_OUTLET_KEY)
  const fieldLabel = readFieldLabel(product, TARGET_OUTLET_KEY)
  const propertyEntry = readPropertyEntry(product, TARGET_OUTLET_KEY)
  const tokens: string[] = []

  pushOutletToken(
    tokens,
    product[TARGET_OUTLET_KEY],
    options?.outletOptionsByID,
  )
  pushOutletToken(tokens, fieldValue, options?.outletOptionsByID)
  pushOutletToken(tokens, propertyEntry?.values, options?.outletOptionsByID)

  const searchText = tokens.join(' ')
  const firstToken = tokens.find((token) => token.trim().length > 0) ?? ''
  const option = options?.outletOptionsByID?.[firstToken]
  const value = normalizeLabel(option?.value) || normalizeLabel(firstToken)
  const label =
    resolveOutletOptionLabel(option, options?.outletOptionsByID) ||
    fieldLabel ||
    value

  return {
    label,
    value,
    family: resolveReportingOutletFamily(searchText),
    scope: resolveReportingOutletScope(searchText),
  }
}

const addWarning = (
  warnings: string[],
  condition: boolean,
  warning: string,
) => {
  if (condition) warnings.push(warning)
}

const buildValorizationAllocations = ({
  quantity,
  volumeM3,
  totalWeightKg,
  reusableWeightKg,
  reuseRatioPercent,
  targetOutlet,
}: {
  quantity: number | null
  volumeM3: number | null
  totalWeightKg: number | null
  reusableWeightKg: number | null
  reuseRatioPercent: number | null
  targetOutlet: ReportingOutletResolution
}) => {
  const empty = {
    reuseInSituQuantity: 0,
    reuseInSituVolumeM3: 0,
    reuseInSituWeightKg: 0,
    reuseExSituQuantity: 0,
    reuseExSituVolumeM3: 0,
    reuseExSituWeightKg: 0,
    recyclingQuantity: 0,
    recyclingVolumeM3: 0,
    recyclingWeightKg: 0,
    recoveryQuantity: 0,
    recoveryVolumeM3: 0,
    recoveryWeightKg: 0,
    disposalQuantity: 0,
    disposalVolumeM3: 0,
    disposalWeightKg: 0,
  }

  const allocate = (value: number | null, ratio: number): number =>
    value === null ? 0 : round(value * ratio)

  if (targetOutlet.family === 'reuse') {
    const reuseWeight = reusableWeightKg ?? 0
    const reuseRatio = reuseRatioPercent === null ? 0 : reuseRatioPercent / 100

    return {
      ...empty,
      reuseInSituQuantity:
        targetOutlet.scope === 'in-situ' ? allocate(quantity, reuseRatio) : 0,
      reuseInSituVolumeM3:
        targetOutlet.scope === 'in-situ' ? allocate(volumeM3, reuseRatio) : 0,
      reuseInSituWeightKg:
        targetOutlet.scope === 'in-situ' ? round(reuseWeight) : 0,
      reuseExSituQuantity:
        targetOutlet.scope === 'ex-situ' ? allocate(quantity, reuseRatio) : 0,
      reuseExSituVolumeM3:
        targetOutlet.scope === 'ex-situ' ? allocate(volumeM3, reuseRatio) : 0,
      reuseExSituWeightKg:
        targetOutlet.scope === 'ex-situ' ? round(reuseWeight) : 0,
    }
  }

  if (targetOutlet.family === 'recycling') {
    return {
      ...empty,
      recyclingQuantity: allocate(quantity, 1),
      recyclingVolumeM3: allocate(volumeM3, 1),
      recyclingWeightKg: totalWeightKg === null ? 0 : round(totalWeightKg),
    }
  }
  if (targetOutlet.family === 'recovery') {
    return {
      ...empty,
      recoveryQuantity: allocate(quantity, 1),
      recoveryVolumeM3: allocate(volumeM3, 1),
      recoveryWeightKg: totalWeightKg === null ? 0 : round(totalWeightKg),
    }
  }
  if (targetOutlet.family === 'disposal') {
    return {
      ...empty,
      disposalQuantity: allocate(quantity, 1),
      disposalVolumeM3: allocate(volumeM3, 1),
      disposalWeightKg: totalWeightKg === null ? 0 : round(totalWeightKg),
    }
  }

  return empty
}

const buildOperationalValorizationRow = (
  product: AdaptedProduct,
  options?: BuildOperationalValorizationTableOptions,
): OperationalValorizationTableRow => {
  const quantity = readQuantity(product)
  const unitWeightKg = readNumericField(product, 'weight')
  const volumeM3 = readVolumeM3(product)
  const co2Impact = readNumericField(product, 'co2Impact')
  const reuseRatioRaw = readNumericField(product, 'reuse_ratio')
  const reuseRatioPercent =
    reuseRatioRaw === null ? null : round(clampPercent(reuseRatioRaw), 2)
  const totalWeightKg =
    quantity !== null && unitWeightKg !== null
      ? round(quantity * unitWeightKg)
      : null
  const totalWeightTonnes =
    totalWeightKg !== null ? round(totalWeightKg / 1000) : null
  const carbonGainKgCo2e =
    quantity !== null && co2Impact !== null ? round(quantity * co2Impact) : null
  const reusableWeightKg =
    totalWeightKg !== null && reuseRatioPercent !== null
      ? round(totalWeightKg * (reuseRatioPercent / 100))
      : null
  const nonReusedWeightKg =
    totalWeightKg !== null && reusableWeightKg !== null
      ? round(totalWeightKg - reusableWeightKg)
      : null
  const targetOutlet = resolveTargetOutlet(product, options)
  const allocations = buildValorizationAllocations({
    quantity,
    volumeM3,
    totalWeightKg,
    reusableWeightKg,
    reuseRatioPercent,
    targetOutlet,
  })
  const unscopedReuseWeightKg =
    targetOutlet.family === 'reuse' && targetOutlet.scope === 'unknown'
      ? (reusableWeightKg ?? 0)
      : 0
  const totalValorizedWeightKg = round(
    unscopedReuseWeightKg +
      allocations.reuseInSituWeightKg +
      allocations.reuseExSituWeightKg +
      allocations.recyclingWeightKg +
      allocations.recoveryWeightKg,
  )
  const valorizationRate =
    totalWeightKg !== null && totalWeightKg > 0
      ? round(totalValorizedWeightKg / totalWeightKg, 4)
      : null
  const warnings: string[] = []

  addWarning(warnings, quantity === null, 'missing_quantity')
  addWarning(warnings, unitWeightKg === null, 'missing_weight')
  addWarning(
    warnings,
    targetOutlet.family === 'unknown',
    'missing_target_outlet',
  )
  addWarning(
    warnings,
    targetOutlet.family === 'reuse' && reuseRatioPercent === null,
    'missing_reuse_ratio',
  )
  addWarning(
    warnings,
    targetOutlet.family === 'reuse' && targetOutlet.scope === 'unknown',
    'missing_target_outlet_scope',
  )

  return {
    id: product.id,
    productID: product.productID,
    lotLabel: readProductString(product, 'lot'),
    resourceNumber: readProductString(product, 'ref') || product.productID,
    resourceLabel: readProductString(product, 'name'),
    categoryLabel: readProductString(product, 'category'),
    quantity,
    quantityUnit: readQuantityUnit(product),
    volumeM3,
    unitWeightKg,
    totalWeightKg,
    totalWeightTonnes,
    carbonGainKgCo2e,
    reuseRatioPercent,
    reusableWeightKg,
    nonReusedWeightKg,
    targetOutlet,
    ...allocations,
    totalValorizedWeightKg,
    valorizationRate,
    warnings,
  }
}

const sumRows = (
  rows: OperationalValorizationTableRow[],
  key: keyof Pick<
    OperationalValorizationTableRow,
    | 'totalWeightKg'
    | 'carbonGainKgCo2e'
    | 'reusableWeightKg'
    | 'nonReusedWeightKg'
    | 'reuseInSituWeightKg'
    | 'reuseExSituWeightKg'
    | 'recyclingWeightKg'
    | 'recoveryWeightKg'
    | 'disposalWeightKg'
    | 'totalValorizedWeightKg'
  >,
): number =>
  round(
    rows.reduce((total, row) => {
      const value = row[key]
      return typeof value === 'number' ? total + value : total
    }, 0),
  )

export const buildOperationalValorizationTable = (
  products: AdaptedProduct[],
  options?: BuildOperationalValorizationTableOptions,
): OperationalValorizationTable => {
  const rows = products
    .filter(isReportingResource)
    .map((product) => buildOperationalValorizationRow(product, options))

  const totalWeightKg = sumRows(rows, 'totalWeightKg')
  const totalValorizedWeightKg = sumRows(rows, 'totalValorizedWeightKg')

  return {
    rows,
    totals: {
      totalWeightKg,
      totalWeightTonnes: round(totalWeightKg / 1000),
      carbonGainKgCo2e: sumRows(rows, 'carbonGainKgCo2e'),
      reusableWeightKg: sumRows(rows, 'reusableWeightKg'),
      nonReusedWeightKg: sumRows(rows, 'nonReusedWeightKg'),
      reuseInSituWeightKg: sumRows(rows, 'reuseInSituWeightKg'),
      reuseExSituWeightKg: sumRows(rows, 'reuseExSituWeightKg'),
      recyclingWeightKg: sumRows(rows, 'recyclingWeightKg'),
      recoveryWeightKg: sumRows(rows, 'recoveryWeightKg'),
      disposalWeightKg: sumRows(rows, 'disposalWeightKg'),
      totalValorizedWeightKg,
      valorizationRate:
        totalWeightKg > 0
          ? round(totalValorizedWeightKg / totalWeightKg, 4)
          : null,
    },
  }
}

const buildTextItems = (
  product: AdaptedProduct,
  definitions: readonly { key: string; label: string }[],
): OperationalResourceSheetItem[] =>
  definitions
    .map((definition) => ({
      key: definition.key,
      label: definition.label,
      value: readProductString(product, definition.key),
    }))
    .filter((item) => item.value.length > 0)

const pushItem = (
  items: OperationalResourceSheetItem[],
  item: OperationalResourceSheetItem,
) => {
  if (
    item.value === '' ||
    item.value === null ||
    typeof item.value === 'undefined'
  ) {
    return
  }

  items.push(item)
}

const buildResourceSheet = (
  product: AdaptedProduct,
  options?: BuildOperationalResourceSheetsOptions,
): OperationalResourceSheet => {
  const valorizationRow = buildOperationalValorizationRow(product, options)
  const materialLabels = readProductStringList(product, 'material')
  const pictures = readPictures(product)
  const characteristics: OperationalResourceSheetItem[] = []
  const dimensions: OperationalResourceSheetItem[] = []
  const circularEconomyIndicators: OperationalResourceSheetItem[] = []
  const preparation = buildTextItems(product, RESOURCE_INSTRUCTION_DEFINITIONS)
  const reuseTracks = buildTextItems(product, REUSE_TRACK_DEFINITIONS)
  const warnings = [...valorizationRow.warnings]

  pushItem(characteristics, {
    key: 'category',
    label: 'Catégorie',
    value: valorizationRow.categoryLabel,
  })
  pushItem(characteristics, {
    key: 'lot',
    label: 'Lot',
    value: valorizationRow.lotLabel,
  })
  pushItem(characteristics, {
    key: 'material',
    label: 'Matériaux',
    value: materialLabels.join(', '),
  })
  pushItem(characteristics, {
    key: 'condition',
    label: 'État',
    value: readProductString(product, 'condition'),
  })

  if (valorizationRow.quantity !== null) {
    pushItem(dimensions, {
      key: 'quantity',
      label: 'Quantité',
      value: valorizationRow.quantity,
      unit: valorizationRow.quantityUnit,
    })
  }
  if (valorizationRow.volumeM3 !== null) {
    pushItem(dimensions, {
      key: 'volumeM3',
      label: 'Volume',
      value: valorizationRow.volumeM3,
      unit: 'm3',
    })
  }
  if (valorizationRow.unitWeightKg !== null) {
    pushItem(dimensions, {
      key: 'unitWeightKg',
      label: 'Poids unitaire',
      value: valorizationRow.unitWeightKg,
      unit: 'kg',
    })
  }
  if (valorizationRow.totalWeightKg !== null) {
    pushItem(dimensions, {
      key: 'totalWeightKg',
      label: 'Poids total',
      value: valorizationRow.totalWeightKg,
      unit: 'kg',
    })
  }

  if (valorizationRow.carbonGainKgCo2e !== null) {
    pushItem(circularEconomyIndicators, {
      key: 'carbonGainKgCo2e',
      label: 'Gain carbone',
      value: valorizationRow.carbonGainKgCo2e,
      unit: 'kg CO2e',
    })
  }
  if (valorizationRow.reuseRatioPercent !== null) {
    pushItem(circularEconomyIndicators, {
      key: 'reuseRatioPercent',
      label: 'Part réemployable',
      value: valorizationRow.reuseRatioPercent,
      unit: '%',
    })
  }
  if (valorizationRow.totalValorizedWeightKg > 0) {
    pushItem(circularEconomyIndicators, {
      key: 'totalValorizedWeightKg',
      label: 'Masse valorisée',
      value: valorizationRow.totalValorizedWeightKg,
      unit: 'kg',
    })
  }

  addWarning(warnings, pictures.length === 0, 'missing_pictures')
  addWarning(
    warnings,
    preparation.length === 0,
    'missing_resource_instructions',
  )
  addWarning(warnings, reuseTracks.length === 0, 'missing_reuse_potential')

  return {
    id: product.id,
    productID: product.productID,
    resourceNumber: valorizationRow.resourceNumber,
    title: valorizationRow.resourceLabel,
    lotLabel: valorizationRow.lotLabel,
    categoryLabel: valorizationRow.categoryLabel,
    pictures,
    characteristics,
    dimensions,
    preparation,
    circularEconomyIndicators,
    reuseTracks,
    targetOutlet: valorizationRow.targetOutlet,
    warnings,
  }
}

export const buildOperationalResourceSheets = (
  products: AdaptedProduct[],
  options?: BuildOperationalResourceSheetsOptions,
): OperationalResourceSheet[] =>
  products
    .filter(isReportingResource)
    .map((product) => buildResourceSheet(product, options))
